import React, { useState, useEffect } from 'react';
import {
  CreditCard, DollarSign, CheckCircle2, ShieldCheck, Lock, AlertCircle, Loader, ArrowRight, RefreshCw, FileText, Upload, Clock, Building2
} from 'lucide-react';
import { NonAttorneyDisclaimer } from './legal/Disclosures';

export default function PublicPaymentView({ token }: { token: string }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<{
    invoice: {
      invoiceId: string;
      invoiceNumber: string;
      invoiceDate: string;
      dueDate: string;
      applicantName: string;
      programName: string;
      currency: string;
      total: number;
      amountPaid: number;
      amountDue: number;
      status: string;
      notes?: string;
      items: Array<{
        name: string;
        description: string;
        quantity: number;
        rate: number;
        amount: number;
        discountAmount: number;
      }>;
    };
    workspace: {
      workspaceId: string;
      businessName: string;
      logoUrl: string;
      supportEmail: string;
      stripePublishableKey: string;
      stripeConnected: boolean;
    };
  } | null>(null);

  // Payment Method Mode
  const [methodMode, setMethodMode] = useState<'online' | 'bank_deposit'>('online');

  // Payment Selection State
  const [paymentOption, setPaymentOption] = useState<'full' | 'partial'>('full');
  const [customAmount, setCustomAmount] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Bank Deposit Form State
  const [depositAmount, setDepositAmount] = useState('');
  const [depositDate, setDepositDate] = useState(new Date().toISOString().split('T')[0]);
  const [bankMethodName, setBankMethodName] = useState('Bank Deposit / Wire Transfer');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [depositNotes, setDepositNotes] = useState('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [isSubmittingDeposit, setIsSubmittingDeposit] = useState(false);
  const [depositResult, setDepositResult] = useState<{ paymentId: string; status: string; message: string } | null>(null);

  // Payment Result State (if returned from Stripe Checkout)
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setIsSuccess(true);
    }
    if (urlParams.get('cancelled') === 'true') {
      setIsCancelled(true);
    }

    // Resolve payment link metadata
    const fetchPaymentLinkData = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`/api/public/payment-link/${token}`);
        if (!res.ok) {
          const errJson = await res.json().catch(() => ({}));
          throw new Error(errJson.error || 'Payment link is invalid or expired.');
        }
        const json = await res.json();
        setData(json);
        setCustomAmount(json.invoice.amountDue.toString());
      } catch (err: any) {
        setError(err.message || 'Failed to load payment invoice.');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentLinkData();
  }, [token]);

  const handleBankDepositSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    setValidationError('');

    const maxAmount = data.invoice.amountDue;
    const amountToPay = parseFloat(depositAmount || customAmount || maxAmount.toString());

    if (isNaN(amountToPay) || amountToPay <= 0) {
      setValidationError('Please enter a valid payment amount greater than $0.00.');
      return;
    }


    setIsSubmittingDeposit(true);
    try {
      let proofAttachment = null;
      if (proofFile) {
        if (proofFile.size > 10 * 1024 * 1024) {
          throw new Error('Attachment exceeds maximum allowed size of 10MB.');
        }
        const base64Content = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1] || result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(proofFile);
        });
        proofAttachment = {
          fileName: proofFile.name,
          mimeType: proofFile.type || 'application/octet-stream',
          base64Content,
        };
      }

      const res = await fetch(`/api/public/payment-link/${token}/bank-deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentAmount: amountToPay,
          paymentDate: depositDate,
          paymentMethod: bankMethodName,
          paymentReference: referenceNumber,
          notes: depositNotes,
          proofAttachment,
        }),
      });

      const resJson = await res.json();
      if (!res.ok) {
        throw new Error(resJson.error || 'Failed to submit bank deposit receipt.');
      }

      setDepositResult({
        paymentId: resJson.paymentId,
        status: resJson.status || 'Pending Approval',
        message: resJson.message || 'Bank deposit submitted successfully. Your payment is under review by our finance team.',
      });
    } catch (err: any) {
      setValidationError(err.message || 'Failed to submit bank deposit.');
    } finally {
      setIsSubmittingDeposit(false);
    }
  };

  const handleCheckout = async () => {
    if (!data) return;
    setValidationError('');

    const maxAmount = data.invoice.amountDue;
    const amountToPay = paymentOption === 'full' ? maxAmount : parseFloat(customAmount);

    if (isNaN(amountToPay) || amountToPay <= 0) {
      setValidationError('Please enter a valid payment amount greater than $0.00.');
      return;
    }


    setIsRedirecting(true);
    try {
      const res = await fetch(`/api/public/payment-link/${token}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentAmount: amountToPay }),
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to initialize payment session');
      }

      const checkoutData = await res.json();
      if (checkoutData.checkoutUrl) {
        window.location.href = checkoutData.checkoutUrl;
      } else {
        throw new Error('No checkout redirect URL received.');
      }
    } catch (err: any) {
      setValidationError(err.message || 'Payment initiation failed.');
      setIsRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 text-center space-y-3 max-w-sm w-full">
          <Loader className="h-8 w-8 text-blue-600 animate-spin mx-auto" />
          <h3 className="text-sm font-extrabold text-slate-800">Loading Payment Details...</h3>
          <p className="text-xs text-slate-400">Verifying secure invoice link...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-rose-200 text-center space-y-4 max-w-md w-full">
          <div className="h-12 w-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h3 className="text-base font-extrabold text-slate-900">Payment Link Unavailable</h3>
          <p className="text-xs text-slate-600 leading-relaxed">{error || 'This link may have expired or been revoked.'}</p>
        </div>
      </div>
    );
  }

  const { invoice, workspace } = data;

  if (depositResult) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans">
        <div className="bg-white p-8 rounded-3xl shadow-2xl border border-amber-200 text-center space-y-6 max-w-lg w-full animate-fade-in">
          <div className="h-16 w-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
            <Clock className="h-10 w-10" />
          </div>

          <div className="space-y-1">
            <h2 className="text-2xl font-black text-slate-900 font-display">Payment Submitted — Pending Review</h2>
            <p className="text-xs text-slate-500 leading-relaxed">{depositResult.message}</p>
          </div>

          <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80 text-xs space-y-3 text-left">
            <div className="flex justify-between border-b border-slate-200/60 pb-2">
              <span className="text-slate-500">Reference ID:</span>
              <span className="font-mono font-bold text-slate-900">#{depositResult.paymentId}</span>
            </div>
            <div className="flex justify-between border-b border-slate-200/60 pb-2">
              <span className="text-slate-500">Invoice Number:</span>
              <span className="font-mono font-bold text-slate-900">#{invoice.invoiceNumber}</span>
            </div>
            <div className="flex justify-between border-b border-slate-200/60 pb-2">
              <span className="text-slate-500">Applicant:</span>
              <span className="font-bold text-slate-800">{invoice.applicantName}</span>
            </div>
            <div className="flex justify-between border-b border-slate-200/60 pb-2">
              <span className="text-slate-500">Submitted Method:</span>
              <span className="font-bold text-slate-800">{bankMethodName}</span>
            </div>
            <div className="flex justify-between pt-1">
              <span className="font-bold text-slate-800">Status:</span>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-amber-100 text-amber-800">
                Pending Approval
              </span>
            </div>
          </div>

          <p className="text-[11px] text-slate-400 leading-relaxed">
            Note: Your submitted payment will be verified by an administrator before reducing your outstanding invoice balance.
          </p>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-3xl shadow-2xl border border-emerald-100 text-center space-y-6 max-w-lg w-full animate-fade-in">
          <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-10 w-10" />
          </div>

          <div className="space-y-1">
            <h2 className="text-2xl font-black text-slate-900 font-display">Payment Successful!</h2>
            <p className="text-xs text-slate-500">Your payment has been securely processed via Stripe.</p>
          </div>

          <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80 text-xs space-y-3 text-left">
            <div className="flex justify-between border-b border-slate-200/60 pb-2">
              <span className="text-slate-500">Invoice Number:</span>
              <span className="font-mono font-bold text-slate-900">#{invoice.invoiceNumber}</span>
            </div>
            <div className="flex justify-between border-b border-slate-200/60 pb-2">
              <span className="text-slate-500">Applicant Name:</span>
              <span className="font-bold text-slate-800">{invoice.applicantName}</span>
            </div>
            <div className="flex justify-between border-b border-slate-200/60 pb-2">
              <span className="text-slate-500">Payment Method:</span>
              <span className="font-bold text-slate-800">Stripe Secure Card</span>
            </div>
            <div className="flex justify-between pt-1">
              <span className="font-bold text-slate-800">Remaining Balance:</span>
              <span className="font-mono font-black text-blue-600">${Number(invoice.amountDue || 0).toFixed(2)} {invoice.currency || 'USD'}</span>
            </div>
          </div>

          <p className="text-[11px] text-slate-400">
            A confirmation receipt has been recorded for your application. You may now close this window.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 text-slate-800 font-sans">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Workspace Header */}
        <div className="flex items-center justify-between bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs">
          <div className="flex items-center gap-4">
            {workspace.logoUrl ? (
              <img src={workspace.logoUrl} alt={workspace.businessName} className="h-10 object-contain" />
            ) : (
              <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center text-white font-extrabold text-lg">
                {workspace.businessName.charAt(0)}
              </div>
            )}
            <div>
              <h2 className="text-base font-extrabold text-slate-900">{workspace.businessName}</h2>
              <p className="text-caption text-slate-400">Support: {workspace.supportEmail}</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-emerald-700 font-bold bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200">
            <ShieldCheck className="h-4 w-4" /> 256-Bit SSL Encrypted
          </div>
        </div>

        {isCancelled && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-800 text-xs font-bold flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
            <span>Payment checkout session was cancelled. You can select an amount and try again below.</span>
          </div>
        )}

        {/* Invoice Summary Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs space-y-6">
          <div className="flex justify-between items-start pb-4 border-b border-slate-100">
            <div>
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">Official Invoice</span>
              <h1 className="text-xl font-black text-slate-900 font-mono">#{invoice.invoiceNumber}</h1>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">Candidate / Program</span>
              <p className="text-xs font-bold text-slate-800">{invoice.applicantName}</p>
              <p className="text-caption text-slate-500">{invoice.programName || 'Standard Exchange Program'}</p>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="p-3">Item Description</th>
                  <th className="p-3 text-center">Qty</th>
                  <th className="p-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {(invoice.items || []).map((item, idx) => (
                  <tr key={idx}>
                    <td className="p-3">
                      <div className="font-bold text-slate-800">{item.name}</div>
                      {item.description && <div className="text-[11px] text-slate-400 mt-0.5">{item.description}</div>}
                    </td>
                    <td className="p-3 text-center font-bold">{item.quantity || 1}</td>
                    <td className="p-3 text-right font-mono font-bold">${Number(item.amount ?? (item.rate * (item.quantity || 1)) ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Financial Breakdown */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 space-y-2 text-xs">
            <div className="flex justify-between text-slate-600">
              <span>Invoice Total:</span>
              <span className="font-mono font-bold">${Number(invoice.total || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-emerald-700">
              <span>Amount Paid To Date:</span>
              <span className="font-mono font-bold">-${Number(invoice.amountPaid || 0).toFixed(2)}</span>
            </div>
            <hr className="border-slate-200 my-1" />
            <div className="flex justify-between text-sm font-black text-slate-900">
              <span>Remaining Outstanding Balance:</span>
              <span className="font-mono text-blue-600">${Number(invoice.amountDue || 0).toFixed(2)} {invoice.currency || 'USD'}</span>
            </div>
          </div>

          {/* Payment Selection Block */}
          {invoice.amountDue > 0 ? (
            <div className="space-y-5 pt-2">
              {/* Payment Method Tabs */}
              <div className="flex border-b border-slate-200">
                <button
                  type="button"
                  onClick={() => setMethodMode('online')}
                  className={`pb-3 px-4 text-xs font-bold transition-all border-b-2 flex items-center gap-2 cursor-pointer ${methodMode === 'online' ? 'border-blue-600 text-blue-600 font-black' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                >
                  <CreditCard className="h-4 w-4" />
                  <span>Credit Card (Online)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMethodMode('bank_deposit')}
                  className={`pb-3 px-4 text-xs font-bold transition-all border-b-2 flex items-center gap-2 cursor-pointer ${methodMode === 'bank_deposit' ? 'border-blue-600 text-blue-600 font-black' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                >
                  <Building2 className="h-4 w-4" />
                  <span>Bank Deposit / Proof Upload</span>
                </button>
              </div>

              {validationError && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-bold flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-rose-600 shrink-0" />
                  <span>{validationError}</span>
                </div>
              )}

              {/* MODE 1: ONLINE CARD CHECKOUT */}
              {methodMode === 'online' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentOption === 'full' ? 'border-blue-600 bg-blue-50/50' : 'border-slate-200 bg-white'}`}>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="paymentOption"
                          checked={paymentOption === 'full'}
                          onChange={() => setPaymentOption('full')}
                          className="text-blue-600"
                        />
                        <span className="font-bold text-xs text-slate-900">Full Outstanding Balance</span>
                      </div>
                      <div className="text-sm font-black font-mono text-blue-600 mt-2">
                        ${Number(invoice.amountDue || 0).toFixed(2)} {invoice.currency || 'USD'}
                      </div>
                    </label>

                    <label className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentOption === 'partial' ? 'border-blue-600 bg-blue-50/50' : 'border-slate-200 bg-white'}`}>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="paymentOption"
                          checked={paymentOption === 'partial'}
                          onChange={() => setPaymentOption('partial')}
                          className="text-blue-600"
                        />
                        <span className="font-bold text-xs text-slate-900">Partial Payment</span>
                      </div>
                      <div className="mt-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="Enter custom amount"
                          value={customAmount}
                          onFocus={() => setPaymentOption('partial')}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-lg p-1.5 font-mono font-bold text-xs outline-none focus:border-blue-600"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Amounts above the balance due are held as credit on your account.</p>
                      </div>
                    </label>
                  </div>

                  <button
                    type="button"
                    disabled={isRedirecting || !workspace.stripeConnected}
                    onClick={handleCheckout}
                    className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-extrabold text-sm rounded-xl transition-all cursor-pointer shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
                  >
                    {isRedirecting ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Connecting to Stripe Secure Checkout...</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4" />
                        <span>Proceed to Stripe Checkout (${(paymentOption === 'full' ? Number(invoice.amountDue || 0) : parseFloat(customAmount || '0')).toFixed(2)})</span>
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </button>

                  {!workspace.stripeConnected && (
                    <p className="text-center text-xs text-rose-500 font-bold">
                      Online payments are temporarily unavailable for this workspace. Please use Bank Deposit or contact support.
                    </p>
                  )}
                </div>
              )}

              {/* MODE 2: BANK DEPOSIT / PROOF UPLOAD */}
              {methodMode === 'bank_deposit' && (
                <form onSubmit={handleBankDepositSubmit} className="space-y-4 bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <label className="text-[10px] font-extrabold uppercase text-slate-500 block mb-1">Payment Amount ($) *</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        min="0.01"
                        placeholder={Number(invoice.amountDue || 0).toFixed(2)}
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg p-2 font-mono font-bold outline-none focus:border-blue-600"
                      />
                      <p className="text-[10px] text-slate-400 mt-1">Amounts above the balance due are held as credit on your account.</p>
                    </div>

                    <div>
                      <label className="text-[10px] font-extrabold uppercase text-slate-500 block mb-1">Payment Date *</label>
                      <input
                        type="date"
                        required
                        value={depositDate}
                        onChange={(e) => setDepositDate(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg p-2 outline-none focus:border-blue-600"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-extrabold uppercase text-slate-500 block mb-1">Bank / Method Name *</label>
                      <select
                        value={bankMethodName}
                        onChange={(e) => setBankMethodName(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg p-2 outline-none focus:border-blue-600 font-medium"
                      >
                        <option value="Bank Deposit / Wire Transfer">Bank Wire Transfer</option>
                        <option value="Zelle">Zelle Transfer</option>
                        <option value="Direct Bank Deposit">Direct Bank Deposit</option>
                        <option value="Local Bank Transfer">Local Bank Transfer</option>
                        <option value="Other Manual Payment">Other Manual Method</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[10px] font-extrabold uppercase text-slate-500 block mb-1">Reference / Transaction #</label>
                      <input
                        type="text"
                        placeholder="e.g. TXN-9823412"
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg p-2 font-mono outline-none focus:border-blue-600"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-extrabold uppercase text-slate-500 block mb-1">Notes / Additional Details</label>
                    <textarea
                      rows={2}
                      placeholder="Add any relevant deposit notes..."
                      value={depositNotes}
                      onChange={(e) => setDepositNotes(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs outline-none focus:border-blue-600 resize-none"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-extrabold uppercase text-slate-500 block mb-1">Upload Receipt / Proof of Payment (PDF, JPG, PNG)</label>
                    <div className="border-2 border-dashed border-slate-300 bg-white hover:border-blue-500 rounded-xl p-4 text-center cursor-pointer transition-all">
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                        className="hidden"
                        id="proof-upload-input"
                      />
                      <label htmlFor="proof-upload-input" className="cursor-pointer block space-y-1">
                        <Upload className="h-5 w-5 text-slate-400 mx-auto" />
                        <span className="text-xs font-bold text-slate-700 block">
                          {proofFile ? proofFile.name : 'Click to select or drop receipt file'}
                        </span>
                        <span className="text-[10px] text-slate-400 block">Maximum file size: 10MB</span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmittingDeposit}
                    className="w-full py-3 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:bg-slate-300 text-white font-extrabold text-xs rounded-xl transition-all cursor-pointer shadow-lg shadow-success/20 flex items-center justify-center gap-2"
                  >
                    {isSubmittingDeposit ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Submitting Bank Deposit Proof...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4" />
                        <span>Submit Proof of Payment for Review</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          ) : (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center text-emerald-800 text-xs font-bold">
              This invoice has been fully paid. No further payments are required.
            </div>
          )}
        </div>

        {/* Wave 4 (D-05): non-attorney disclaimer + terms/refund links at
            the point of payment, from the workspace legal config. */}
        <div className="max-w-xl mx-auto w-full px-4 pb-8 space-y-3">
          <NonAttorneyDisclaimer />
          <p className="text-center text-[11px] text-slate-400">
            By paying you agree to the{' '}
            <a href="/terms" target="_blank" rel="noreferrer" className="underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/refund-policy" target="_blank" rel="noreferrer" className="underline">Refund Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
